/**
 * The app renders full-screen on a phone but inside a fixed 414x736 panel on
 * desktop (see .phone-frame in App.css). Plain `@media` queries answer for the
 * *browser window*, so inside that panel every layout rule took the desktop
 * branch at phone width — a third layout that exists on no real device.
 *
 * PhoneFrame measures the panel and stamps pf-narrow / pf-xnarrow / pf-short /
 * pf-xshort on it, so layout rules key off the box the UI actually occupies and
 * resolve identically on a real phone and in the desktop preview.
 *
 * Container queries would do this in pure CSS, but `container-type` makes the
 * panel a containing block for its `position: fixed` descendants, which would
 * trap the floating PiP windows inside the frame on desktop.
 */

/** Panel width at or below which the phone layout applies. */
export const NARROW = 640;
/** Panel width at or below which content goes edge-to-edge / full-bleed. */
export const XNARROW = 480;
/** Panel content height at or below which vertical padding tightens. */
export const SHORT = 720;
/** Panel content height at or below which it tightens again. */
export const XSHORT = 620;

let frameW = 0;
let frameH = 0;

export function setFrameSize(w: number, h: number) {
  frameW = w;
  frameH = h;
}

/** Panel width, falling back to the viewport before the panel has mounted. */
export function frameWidth() {
  return frameW || window.innerWidth;
}

/** Panel content height, falling back to the viewport before mount. */
export function frameHeight() {
  return frameH || window.innerHeight;
}

/**
 * Is this actually a phone, whatever the viewport claims?
 *
 * index.html stamps data-phone before first paint from the device's own
 * properties (touch points, no hover) rather than its width. Chrome's
 * "Request desktop site" is sticky per site and reports a ~980px viewport, so
 * every width test agrees the device is a desktop — which is how a Pixel 8
 * ended up with the desktop panel, the drag handles, the admin export icon and
 * floating PiP windows. Anything choosing between phone and desktop BEHAVIOUR
 * asks this; anything choosing a layout size still measures the box.
 */
export function isPhoneDevice(): boolean {
  return typeof document !== 'undefined'
    && document.documentElement.hasAttribute('data-phone');
}
